/* eslint-disable @typescript-eslint/member-ordering */
import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, input } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { combineLatest, Observable, Subject } from "rxjs";
import { combineLatestWith, filter, map, take, takeUntil } from "rxjs/operators";
import { EngagementsService } from "src/app/services/engagements.service";
import { ErrorCategory, ErrorContainer, ErrorsService } from "src/app/services/errors.service";
import { LoadingStateService } from "src/app/services/loading-state/loading-state.service";
import { getIsNotNullable } from "src/app/utils/utils";
import { ExpandableErrorAlertComponent } from "../expandable-alerts/expandable-error-alert/expandable-error-alert.component";
import { ButtonModule } from "@storeblocks/button-ng";
import { AsyncPipe } from "@angular/common";
import { FmsPipe } from "../../pipes/fms.pipe";
import { AccordionModule } from "@storeblocks/accordion-ng";

@Component({
  selector: "app-expandable-data-alerts",
  templateUrl: "./expandable-data-alerts.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [ExpandableErrorAlertComponent, ButtonModule, AsyncPipe, FmsPipe, AccordionModule],
})
export class ExpandableDataAlertsComponent implements OnInit, OnDestroy {
  private readonly engagementsService: EngagementsService = inject(EngagementsService);
  private readonly errorsService: ErrorsService = inject(ErrorsService);
  private readonly loadingStateService: LoadingStateService = inject(LoadingStateService);

  private readonly isLoaded$ = toObservable(this.loadingStateService.isLoaded);
  private readonly destroy$ = new Subject<void>();

  public readonly category = input<ErrorCategory | ErrorCategory[]>([
    ErrorCategory.Engagement,
    ErrorCategory.LifeExpectancy,
    ErrorCategory.Prognosis,
    ErrorCategory.Consent,
    ErrorCategory.Customer,
  ]);

  public alerts$!: Observable<ErrorContainer[]>;
  public hasAlerts$!: Observable<boolean>;

  private static sortByCategory(a: ErrorContainer, b: ErrorContainer): number {
    if (a.category < b.category) {
      return -1;
    }
    if (a.category > b.category) {
      return 1;
    }
    return 0;
  }

  public ngOnInit(): void {
    this.alerts$ = combineLatest([
      this.engagementsService.allEngagementErrors$.pipe(map((error) => error.filter(getIsNotNullable))),
      this.errorsService.errors$,
    ]).pipe(
      map(([engagementErrors, errors]) => engagementErrors.concat(errors)),
      ErrorsService.categoryFilterPipe(this.category()),
      combineLatestWith(this.isLoaded$.pipe(filter(Boolean), take(1))),
      map(([alerts]) => [...alerts].sort(ExpandableDataAlertsComponent.sortByCategory)),
      takeUntil(this.destroy$),
    );

    this.hasAlerts$ = this.alerts$.pipe(map((alerts) => alerts.length > 0));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public refresh(): void {
    window.location.reload();
  }
}
